import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/* Simple mobile detection hook */
function useIsMobile() {
  var [mobile, setMobile] = useState(window.innerWidth < 1024);
  useEffect(function() {
    function handler() { setMobile(window.innerWidth < 1024); }
    window.addEventListener('resize', handler);
    return function() { window.removeEventListener('resize', handler); };
  }, []);
  return mobile;
}

function useIsSmall() {
  var [small, setSmall] = useState(window.innerWidth < 640);
  useEffect(function() {
    function handler() { setSmall(window.innerWidth < 640); }
    window.addEventListener('resize', handler);
    return function() { window.removeEventListener('resize', handler); };
  }, []);
  return small;
}

var NAV_BASE = [
  { to:'/dashboard',    icon:'🏠', label:'Dashboard',   exact:true, perm:null        },
  { to:'/parties',      icon:'🏢', label:'Parties',                 perm:'parties'   },
  { to:'/trips',        icon:'📦', label:'Trips',                   perm:'trips'     },
  { to:'/transporters', icon:'🚚', label:'Suppliers',               perm:'suppliers' },
  { to:'/drivers',      icon:'👤', label:'Drivers',                 perm:'drivers'   },
  { to:'/vehicles',     icon:'🚛', label:'Trucks',                  perm:'vehicles'  },
  { to:'/expenses-list',icon:'💸', label:'Expenses', badge:'NEW',   perm:'expenses'  },
  { to:'/payments',     icon:'💳', label:'Payments',                perm:'payments'  },
  /* Paisa kahan phansa hai — Reports ke andar daalte to koi dekhta hi nahi;
     ye roz subah khulne wali screen hai, isliye Payments ke saath. */
  /* Money Due ab Dashboard par hai (sabse upar) — menu chhota rakhne ke liye
     yahan se hataya. Page aur uska raasta jaisa tha waisa hi hai. */
  { to:'/ledger',       icon:'📒', label:'Ledger',                  perm:'ledger'    },
  { to:'/banks',        icon:'🏦', label:'Accounts',                perm:'payments'  },
  { to:'/invoices',     icon:'🧾', label:'Invoices',    badge:'NEW',  perm:'trips'     },
  { to:'/challans',     icon:'📑', label:'Challans',    badge:'NEW',  perm:'trips'     },
  { to:'/reports',      icon:'📊', label:'Reports',                 perm:'reports'   },
];
var NAV_ADMIN_ONLY = [
  { to:'/help',            icon:'🎧', label:'Help & Support', badge:'NEW', perm:null        },
  { to:'/settings/email',  icon:'📧', label:'Email Settings',  perm:'__admin__' },
  { to:'/users',           icon:'👥', label:'Staff & Users',   perm:'__admin__' },
];

/* Supplier ka module purane data me 'transporters' naam se bhi pada hai —
   backend dono ko ek maanta hai, menu bhi maane. */
var PERM_ALIAS = { suppliers:'transporters', transporters:'suppliers' };

function canView(user, perm) {
  if (!perm) return true;
  if (!user) return false;
  // Admin-only items — only show for admin role
  if (perm === '__admin__') return user.role === 'admin';
  // Admin always sees everything
  if (user.role === 'admin') return true;
  var p = user.permissions;
  /* Koi tick set nahi — NULL ho ya khaali {} — dono ka matlab backend ke
     liye "sirf dekho" hai. Pehle {} par ye poora menu gayab kar deta tha
     aur staff ko sirf Dashboard dikhta tha, jabki API use padhne deti
     thi. Dono ek baat kahein. */
  if (!p || !Object.keys(p).length) return true;
  var m = p[perm] || p[PERM_ALIAS[perm]];
  if (!m) return false;
  return !!m.view;
}


/* Subscription state, worked out in one place */
function subInfo(user) {
  if (!user) return null;
  var plan = user.subscription_plan;
  if (plan === 'lifetime' || !user.subscription_expires) return null;   // nothing to show
  var dl = Math.ceil((new Date(user.subscription_expires) - new Date()) / 86400000);
  var isTrial = plan === 'trial';
  if (dl < 0)  return { kind:'expired', days:Math.abs(dl), isTrial:isTrial };
  if (isTrial) return { kind:'trial',   days:dl, isTrial:true };
  if (dl <= 10) return { kind:'ending', days:dl, isTrial:false };       // paid, but ending soon
  return null;
}


/* ══════════════════════════════════════════════════════════
   AUTO UPDATE
   ──────────────────────────────────────────────────────────
   Every build gives main.<hash>.js a new name. We fetch
   index.html with cache disabled and compare the hash it points
   to against the script this page actually loaded. Different
   hash means a newer build is live.

   No version number to remember and bump — the hash does it.
   Users stop having to clear their cache by hand.
══════════════════════════════════════════════════════════ */
function currentBundle() {
  var el = Array.prototype.slice.call(document.querySelectorAll('script[src]'))
    .map(function (s) { return s.getAttribute('src') || ''; })
    .filter(function (s) { return s.indexOf('/static/js/main.') !== -1; })[0];
  if (!el) return null;
  var m = el.match(/main\.[\w]+\.js/);
  return m ? m[0] : null;
}

async function newBuildAvailable() {
  var mine = currentBundle();
  if (!mine) return false;                     // dev server, nothing to compare
  try {
    var res = await fetch('/index.html?_=' + Date.now(), { cache: 'no-store' });
    if (!res.ok) return false;
    var html = await res.text();
    var m = html.match(/main\.[\w]+\.js/);
    return !!(m && m[0] !== mine);
  } catch (e) { return false; }
}

function roleColor(role) {
  if (role==='admin')      return { bg:'#fee2e2', color:'#dc2626' };
  if (role==='accountant') return { bg:'#faf5ff', color:'#7c3aed' };
  return { bg:'#eff6ff', color:'#2563eb' };
}

function SidebarContent({ onClose }) {
  var navigate = useNavigate();
  var { user, logout } = useAuth();
  function handleLogout() { logout(); navigate('/login'); }

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#fff', borderRight:'1px solid #e8ecef' }}>

      {/* Logo */}
      <div style={{ padding:'14px 16px', borderBottom:'1px solid #f0f0f0', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          {user && user.logo_url ? (
            <img src={user.logo_url} alt="Logo"
              style={{ width:'36px', height:'36px', borderRadius:'50%', objectFit:'cover', border:'1px solid #e5e7eb', background:'#fff', padding:'2px' }}
            />
          ) : (
            <div style={{ width:'32px', height:'32px', borderRadius:'8px', background:'linear-gradient(135deg,#16a34a,#22c55e)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px' }}>🚛</div>
          )}
          <div>
            <div style={{ fontSize:'13px', fontWeight:'800', color:'#111827', lineHeight:1.2, maxWidth:'130px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {/* Purana naam "TransportBook" yahan reh gaya tha — jiska
                  company_name set nahi, use sidebar me wahi dikhta tha.
                  Seedhi grep se nahi milta tha kyunki naam tag se kata
                  hua hai; file me "TransportBook" ek saath likha hi nahi. */}
              {user && user.company_name ? user.company_name : <>Transport<span style={{ color:'#15803d' }}>Master</span></>}
            </div>
            {/* "Pro Platform" bhi purane naam "TransportBook Pro" ka hissa
                tha. Ab company ke naam ke neeche SOFTWARE ka naam — jisse
                aadmi ko pata rehta hai ki chala kis par raha hai. */}
            <div style={{ fontSize:'9px', color:'#9ca3af', fontWeight:'600', letterSpacing:'0.8px', textTransform:'uppercase' }}>TransportMaster</div>
          </div>
        </div>
        {onClose && (
          <button className="icon-btn" onClick={onClose} style={{ background:'#f3f4f6', border:'none', borderRadius:'6px', width:'26px', height:'26px', cursor:'pointer', color:'#6b7280', fontSize:'13px', fontWeight:'700', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex:1, padding:'6px 8px', overflowY:'auto' }}>
        {[...NAV_BASE, ...NAV_ADMIN_ONLY].filter(function(item){ return canView(user, item.perm); }).map(function(item) {
          /* Plan khatam ho to sirf Dashboard khula; baaki har cheez
             seedha renew wale page par le jaati hai. */
          var si0 = subInfo(user);
          var locked = !!(si0 && si0.kind === 'expired') && item.to !== '/dashboard';
          return (
            <NavLink key={item.to} to={locked ? '/renew' : item.to} end={locked ? false : (item.exact?true:false)}
              onClick={onClose}
              style={function(p) {
                var base = { display:'flex', alignItems:'center', gap:'9px', padding:'8px 10px', borderRadius:'7px', marginBottom:'1px', fontSize:'13px', fontWeight:'500', textDecoration:'none', color:'#6b7280', transition:'all 0.12s', position:'relative' };
                if (p.isActive) return Object.assign({}, base, { background:'#f0fdf4', color:'#16a34a', fontWeight:'600' });
                return base;
              }}
              onMouseEnter={function(e) { if (!e.currentTarget.style.background.includes('f0fdf4')) { e.currentTarget.style.background='#f9fafb'; e.currentTarget.style.color='#374151'; } }}
              onMouseLeave={function(e) { if (!e.currentTarget.style.background.includes('f0fdf4')) { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#6b7280'; } }}
            >
              <span style={{ fontSize:'15px', width:'18px', textAlign:'center', flexShrink:0, opacity: locked ? 0.45 : 1 }}>{item.icon}</span>
              <span style={{ flex:1, opacity: locked ? 0.45 : 1 }}>{item.label}</span>
              {locked && <span style={{ fontSize:'11px', opacity:0.6 }}>🔒</span>}
              {item.badge && <span style={{ fontSize:'9px', fontWeight:'700', padding:'1px 5px', borderRadius:'3px', background:'#16a34a', color:'#fff' }}>{item.badge}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Bottom */}
      <div style={{ padding:'6px 8px 10px', borderTop:'1px solid #f0f0f0', flexShrink:0 }}>
        <NavLink to="/profile" onClick={onClose}
          style={function(p){ var b={display:'flex',alignItems:'center',gap:'9px',padding:'8px 10px',borderRadius:'7px',marginBottom:'1px',fontSize:'13px',fontWeight:'500',textDecoration:'none',color:'#6b7280',transition:'all 0.12s'}; if(p.isActive)return Object.assign({},b,{background:'#f0fdf4',color:'#16a34a',fontWeight:'600'});return b; }}
        >
          <span style={{ fontSize:'15px', width:'18px', textAlign:'center' }}>⚙️</span>
          <span>Settings</span>
        </NavLink>
        <button onClick={handleLogout}
          style={{ width:'100%', display:'flex', alignItems:'center', gap:'9px', padding:'8px 10px', borderRadius:'7px', fontSize:'13px', fontWeight:'500', color:'#dc2626', background:'none', border:'none', cursor:'pointer', transition:'all 0.12s' }}
          onMouseEnter={function(e){ e.currentTarget.style.background='#fef2f2'; }}
          onMouseLeave={function(e){ e.currentTarget.style.background='transparent'; }}
        >
          <span style={{ fontSize:'15px', width:'18px', textAlign:'center' }}>⏻</span>
          <span>Logout</span>
        </button>
      </div>

      {/* Footer */}
      <div style={{ padding:'8px 16px 12px', borderTop:'1px solid #f0f0f0', display:'flex', alignItems:'center', gap:'6px', flexShrink:0 }}>
        <span style={{ fontSize:'13px' }}>🔒</span>
        <span style={{ fontSize:'10px', color:'#9ca3af', fontWeight:'500' }}>100% Safe & Secure</span>
      </div>
    </div>
  );
}

export default function Layout() {
  var { user } = useAuth();
  var navigate = useNavigate();
  var isMobile = useIsMobile();
  var isSmall  = useIsSmall();
  var [open, setOpen] = useState(false);

  /* Close drawer on desktop resize */
  useEffect(function() { if (!isMobile) setOpen(false); }, [isMobile]);

  var name    = user ? (user.name||'User')     : 'User';
  var phone   = user ? (user.phone||'')        : '';
  var initials = name.split(' ').map(function(w){ return w[0]; }).join('').toUpperCase().slice(0,2)||'U';
  var [updateReady, setUpdateReady] = React.useState(false);

  React.useEffect(function () {
    var stopped = false;
    function check() {
      if (stopped || document.hidden) return;
      newBuildAvailable().then(function (yes) { if (!stopped && yes) setUpdateReady(true); });
    }
    check();
    // Every 10 minutes, and whenever the user comes back to the tab
    var t = setInterval(check, 10 * 60 * 1000);
    document.addEventListener('visibilitychange', check);
    return function () {
      stopped = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  var rc = roleColor(user ? user.role : '');

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100vh', overflow:'hidden', background:'#f4f5f7' }}>

    {/* A newer build is live — offer a one-tap reload */}
    {updateReady && (
      <div style={{ flexShrink:0, background:'#1d4ed8', color:'#fff', padding:'8px 14px',
        display:'flex', alignItems:'center', justifyContent:'center', gap:'12px', flexWrap:'wrap' }}>
        <span style={{ fontSize:'12.5px', fontWeight:'700' }}>
          A new version is available
        </span>
        <button onClick={function(){
            // Drop any service-worker caches, then hard-reload
            if (window.caches && caches.keys) {
              caches.keys().then(function(ks){ return Promise.all(ks.map(function(k){ return caches.delete(k); })); })
                .catch(function(){})
                .then(function(){ window.location.reload(true); });
            } else { window.location.reload(true); }
          }}
          style={{ padding:'5px 14px', minHeight:'32px', borderRadius:'6px', background:'#fff',
            border:'none', color:'#1d4ed8', fontSize:'12px', fontWeight:'800',
            cursor:'pointer', whiteSpace:'nowrap' }}>
          Reload
        </button>
      </div>
    )}

    {/* Trial / subscription bar */}
    {(function(){
      var si = subInfo(user);
      if (!si) return null;
      var C = {
        expired: { bg:'#b91c1c', text:'#fff', cta:'#fff', ctaText:'#b91c1c' },
        trial:   { bg: si.days <= 3 ? '#c2410c' : '#0f766e', text:'#fff', cta:'#fff', ctaText:'#0f172a' },
        ending:  { bg:'#c2410c', text:'#fff', cta:'#fff', ctaText:'#9a3412' }
      }[si.kind];
      var msg = si.kind === 'expired'
        ? (si.isTrial ? 'Your 15-day free trial has ended'
                      : 'Your subscription has ended')
        : si.kind === 'trial'
          ? 'Free trial — ' + si.days + ' days left'
          : 'Subscription ends in ' + si.days + ' days';
      var short = si.kind === 'expired' ? (si.isTrial ? 'Trial ended' : 'Plan ended')
                                        : si.days + 'd left';

      return (
        <div style={{ flexShrink:0, background:C.bg, color:C.text, padding:'7px 12px',
          display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', flexWrap:'nowrap' }}>
          <span className="sub-msg-full" style={{ fontSize:'12.5px', fontWeight:'700', minWidth:0,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {si.kind === 'expired' ? '🔴' : '⏳'} {msg}
          </span>
          <span className="sub-msg-short" style={{ display:'none', fontSize:'12px', fontWeight:'700', whiteSpace:'nowrap' }}>
            {si.kind === 'expired' ? '🔴' : '⏳'} {short}
          </span>
          <Link to="/renew"
            style={{ flexShrink:0, padding:'4px 11px', minHeight:'30px', display:'inline-flex', alignItems:'center',
              borderRadius:'6px', background:C.cta, color:C.ctaText, fontSize:'11.5px',
              fontWeight:'800', textDecoration:'none', whiteSpace:'nowrap' }}>
            {si.kind === 'expired' ? 'Renew now' : 'Upgrade'}
          </Link>
        </div>
      );
    })()}

    {/* Super admin is inside someone's account — make it impossible to forget */}
    {user && user.impersonated && (
      <div style={{ flexShrink:0, background:'#b91c1c', color:'#fff', padding:'8px 14px',
        display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px', flexWrap:'wrap' }}>
        <div style={{ fontSize:'12.5px', fontWeight:'700' }}>
          ⚠️ You are inside <b>{user.name}</b>'s account (super admin). Anything you do affects their data.
        </div>
        <button onClick={function(){ localStorage.removeItem('tmaster_token'); window.location.href='/super-admin'; }}
          style={{ padding:'5px 12px', minHeight:'32px', borderRadius:'6px', background:'#fff',
            border:'none', color:'#b91c1c', fontSize:'12px', fontWeight:'800', cursor:'pointer', whiteSpace:'nowrap' }}>
          Exit
        </button>
      </div>
    )}

    <div style={{ display:'flex', flex:1, overflow:'hidden', minHeight:0 }}>

      {/* Desktop sidebar */}
      {!isMobile && (
        <div style={{ width:'210px', flexShrink:0, height:'100vh' }}>
          <SidebarContent />
        </div>
      )}

      {/* Mobile overlay drawer */}
      {isMobile && open && (
        <div style={{ position:'fixed', inset:0, zIndex:50, display:'flex' }}>
          <div style={{ width:'210px', flexShrink:0, height:'100vh' }}>
            <SidebarContent onClose={function(){ setOpen(false); }}/>
          </div>
          <div style={{ flex:1, background:'rgba(0,0,0,0.4)' }} onClick={function(){ setOpen(false); }}/>
        </div>
      )}

      {/* Main content */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>

        {/* Topbar */}
        <header className="topbar" style={{ padding:'0 16px', display:'flex', alignItems:'center', gap:'10px', flexShrink:0 }}>
          {isMobile && (
            <button onClick={function(){ setOpen(true); }} className="icon-btn" aria-label="Menu"
              style={{ background:'#f3f4f6', border:'none', borderRadius:'9px', width:'40px', height:'40px', cursor:'pointer', color:'#374151', fontSize:'18px', flexShrink:0 }}>☰</button>
          )}

          <div style={{ flex:1 }}/>

          {/* Plan ka chip — expire se pehle aur uske baad, dono waqt
              yahin se recharge ho jaata hai. Settings mein dhoondhna
              nahi padta. Plan aaram se chal raha ho to kuch nahi dikhta. */}
          {(function () {
            var si2 = subInfo(user);
            if (!si2) return null;
            var over = si2.kind === 'expired';
            var warn = si2.kind !== 'ending' || si2.days <= 3;
            var col = over ? { bg:'#fef2f2', bd:'#fecaca', tx:'#b91c1c' }
                    : warn ? { bg:'#fff7ed', bd:'#fed7aa', tx:'#c2410c' }
                           : { bg:'#f0fdf4', bd:'#bbf7d0', tx:'#15803d' };
            return (
              <Link to="/renew" title="Plan and payments"
                style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'5px 11px', minHeight:'32px',
                  borderRadius:'999px', background:col.bg, border:'1px solid ' + col.bd, color:col.tx,
                  fontSize:'12px', fontWeight:'800', textDecoration:'none', whiteSpace:'nowrap', flexShrink:0 }}>
                <span>{over ? '🔴' : '⏳'}</span>
                <span className="plan-chip-full">{over ? 'Plan ended — Renew' : si2.days + ' days left — Renew'}</span>
                <span className="plan-chip-short" style={{ display:'none' }}>{over ? 'Renew' : si2.days + 'd'}</span>
              </Link>
            );
          })()}

          {/* New Trip CTA — sirf bade parde par.
               Tablet/phone par neeche ki patti ka bada hara "+" wahi kaam karta hai,
               aur page par apna "+ New Trip" bhi hota hai — teen jagah ek hi cheez
               dikhti thi. Kuch hataya nahi, bas bade parde tak rakha. */}
          {!isMobile && !isSmall && (
            <button onClick={function(){ navigate('/trips/new'); }}
              style={{ padding:'7px 14px', borderRadius:'7px', background:'#16a34a', border:'none', color:'#fff', fontSize:'12px', fontWeight:'600', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px', transition:'all 0.12s', flexShrink:0 }}
              onMouseEnter={function(e){ e.currentTarget.style.background='#15803d'; }}
              onMouseLeave={function(e){ e.currentTarget.style.background='#16a34a'; }}
            >+ New Trip</button>
          )}

          {/* User info — topbar ONLY */}
          <div onClick={function(){ navigate('/profile'); }}
            style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer', padding:'5px 8px', borderRadius:'8px', transition:'all 0.12s', flexShrink:0 }}
            onMouseEnter={function(e){ e.currentTarget.style.background='#f3f4f6'; }}
            onMouseLeave={function(e){ e.currentTarget.style.background='transparent'; }}
          >
            {user && user.logo_url ? (
              <img src={user.logo_url} alt="Logo"
                style={{ width:'30px', height:'30px', borderRadius:'50%', objectFit:'cover', border:'2px solid #16a34a', flexShrink:0 }}/>
            ) : (
              <div style={{ width:'30px', height:'30px', borderRadius:'50%', background:'#16a34a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:'700', color:'#fff', flexShrink:0 }}>{initials}</div>
            )}
            <div className="topbar-name">
                <div style={{ fontSize:'12px', fontWeight:'600', color:'#111827', lineHeight:1.2, maxWidth:'120px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{name}</div>
                <div style={{ fontSize:'10px', color:'#9ca3af' }}>{phone}</div>
              </div>
            <span style={{ fontSize:'9px', fontWeight:'700', padding:'2px 7px', borderRadius:'20px', background:rc.bg, color:rc.color, flexShrink:0, border:'1px solid transparent' }}>
              {(user?user.role:'').toUpperCase()}
            </span>
          </div>
        </header>

        {/* Subscription expiry warning */}
      {user && user.role !== 'admin' && user.subscription_expires && (function(){
        var days = Math.ceil((new Date(user.subscription_expires) - new Date()) / (1000*60*60*24));
        if (days > 7) return null;
        if (days < 0) return (
          <div style={{ background:'#dc2626', color:'#fff', padding:'8px 16px', textAlign:'center', fontSize:'12px', fontWeight:'700', zIndex:10 }}>
            🔴 Your subscription expired {Math.abs(days)} days ago. Contact your admin to renew.
          </div>
        );
        return (
          <div style={{ background:'#f59e0b', color:'#fff', padding:'8px 16px', textAlign:'center', fontSize:'12px', fontWeight:'700', zIndex:10 }}>
            ⚠️ Subscription expires in {days} day{days===1?'':'s'}. Contact your admin to renew.
          </div>
        );
      })()}

      {/* Page content */}
        <main style={{ flex:1, overflowY:'auto', background:'#f4f5f7' }}>
          <Outlet/>
          {/* room so the last card is not hidden behind the tab bar */}
          {isMobile && <div className="bottom-nav-space"/>}
        </main>
      </div>

      {/* Phone / tablet: app-style tab bar. The floating "+" that used to
          sit over the content is the centre button here. The full menu
          stays behind ☰ at the top. */}
      {isMobile && (
        <nav className="bottom-nav" aria-label="Main">
          {[
            { to:'/dashboard', ico:'🏠', label:'Home',     perm:null },
            { to:'/trips',     ico:'📦', label:'Trips',    perm:'trips' },
            { add:true },
            { to:'/parties',   ico:'🏢', label:'Parties',  perm:'parties' },
            { to:'/payments',  ico:'💳', label:'Payments', perm:'payments' }
          ].map(function(it, i){
            if (it.add) return canView(user, 'trips') ? (
              <button key="add" type="button" className="bn-add" aria-label="New trip"
                onClick={function(){ navigate('/trips/new'); }}>+</button>
            ) : null;
            if (!canView(user, it.perm)) return null;
            return (
              <NavLink key={it.to} to={it.to} end={it.to === '/dashboard'}
                className={function(p){ return p.isActive ? 'active' : ''; }}>
                <span className="bn-ico">{it.ico}</span>{it.label}
              </NavLink>
            );
          })}
        </nav>
      )}
    </div>
    </div>
  );
}
